import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, Check } from "lucide-react";
import { ACTION_BTN_WHITE, ACTION_BTN_ICON } from "../../../styles/actionButtons";

const PRESETS = [
  { key: "all", label: "All time" },
  { key: "month", label: "This month" },
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last 90 days" },
  { key: "year", label: "This year" },
];

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const computeRange = (key) => {
  const now = new Date();
  const end = toYMD(now);
  if (key === "all") return { from: "", to: "" };
  if (key === "month")
    return { from: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), to: end };
  if (key === "year")
    return { from: toYMD(new Date(now.getFullYear(), 0, 1)), to: end };
  const days = parseInt(key, 10);
  const s = new Date(now);
  s.setDate(s.getDate() - days + 1);
  return { from: toYMD(s), to: end };
};

const fmt = (s) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

export default function DateRangeFilter({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const label = from && to ? `${fmt(from)} – ${fmt(to)}` : "All time";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={ACTION_BTN_WHITE}
      >
        <Calendar className={`${ACTION_BTN_ICON} text-slate-400`} />
        <span className="whitespace-nowrap">{label}</span>
        <ChevronDown className={`${ACTION_BTN_ICON} text-slate-400`} />
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-2 shadow-xl">
          {PRESETS.map((p) => {
            const selected = p.key === "all" && !from && !to;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  const r = computeRange(p.key);
                  onChange(r.from, r.to);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                {p.label}
                {selected && <Check size={16} className="text-slate-900" />}
              </button>
            );
          })}
          <div className="mt-1 border-t border-slate-100 pt-2">
            <p className="px-3 pb-1.5 text-xs font-medium text-slate-400">
              Custom range
            </p>
            <div className="flex items-center gap-1.5 px-3 pb-1">
              <input
                type="date"
                value={from || ""}
                onChange={(e) => onChange(e.target.value, to)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 [color-scheme:light] focus:border-yellow-400 focus:outline-none"
              />
              <span className="text-slate-400">–</span>
              <input
                type="date"
                value={to || ""}
                onChange={(e) => onChange(from, e.target.value)}
                className="w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 [color-scheme:light] focus:border-yellow-400 focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
